import { CHARACTER_BY_ID } from "./content";
import type { EntityState } from "./types";

/** Arcade-readable name colors for duplicate fighters in one room. */
export const PLAYER_NAME_COLORS = [
  "#f7e05e",
  "#70efff",
  "#ff6b9d",
  "#7dff8a",
  "#ff9a32",
  "#c59bff",
  "#ff4d4d",
  "#ffffff",
  "#4da6ff",
  "#ffd166"
] as const;

function normalizeHex(value: string): string {
  return value.trim().toLowerCase();
}

function takeColor(preferred: string | undefined, used: Set<string>): string {
  const candidates = preferred ? [preferred, ...PLAYER_NAME_COLORS] : [...PLAYER_NAME_COLORS];
  for (const candidate of candidates) {
    const key = normalizeHex(candidate);
    if (used.has(key)) continue;
    used.add(key);
    return candidate;
  }
  const fallback = PLAYER_NAME_COLORS[used.size % PLAYER_NAME_COLORS.length] ?? "#ffffff";
  used.add(normalizeHex(fallback));
  return fallback;
}

export function playerColorForSlot(slot: number): string {
  return PLAYER_NAME_COLORS[((slot % PLAYER_NAME_COLORS.length) + PLAYER_NAME_COLORS.length) % PLAYER_NAME_COLORS.length] ?? "#ffffff";
}

export function nextUnusedPlayerColor(usedColors: readonly string[]): string {
  const used = new Set(usedColors.map((value) => normalizeHex(value)));
  return PLAYER_NAME_COLORS.find((candidate) => !used.has(normalizeHex(candidate))) ?? playerColorForSlot(usedColors.length);
}

export function resolvePlayerNameColors(players: readonly EntityState[]): Map<string, string> {
  const colors = new Map<string, string>();
  const groups = new Map<string, EntityState[]>();
  const reserved = new Set<string>();

  for (const player of [...players].sort((first, second) => first.spawnOrder - second.spawnOrder || first.id.localeCompare(second.id))) {
    if (!player.nameColor) continue;
    colors.set(player.id, player.nameColor);
    reserved.add(normalizeHex(player.nameColor));
  }

  for (const player of players) {
    if (colors.has(player.id)) continue;
    const group = groups.get(player.definitionId) ?? [];
    group.push(player);
    groups.set(player.definitionId, group);
  }

  for (const group of groups.values()) {
    group.sort((first, second) => first.spawnOrder - second.spawnOrder || first.id.localeCompare(second.id));
    const accent = CHARACTER_BY_ID.get(group[0]?.definitionId ?? "")?.palette.accent ?? "#ffffff";
    if (group.length === 1) {
      colors.set(group[0]!.id, takeColor(accent, reserved));
      continue;
    }

    const used = new Set(reserved);
    for (const [index, player] of group.entries()) {
      const color = takeColor(index === 0 ? accent : undefined, used);
      colors.set(player.id, color);
      reserved.add(normalizeHex(color));
    }
  }

  return colors;
}
